import { z } from 'zod';

/**
 * Shape validation for a Monoceros solution-config yml. Catalog
 * validation (which languages/services actually exist) happens
 * separately in `apply`, against `create/catalog.ts` — that keeps the
 * schema decoupled from the catalog and lets the schema live without
 * pulling the whole devcontainer scaffold module in.
 *
 * Schema mirrors the StackFile shape from `create/types.ts` except:
 *
 *   - `features` is an **array** of `{ ref, options }` entries (yml is
 *     edited by humans and arrays diff/comment better than maps).
 *     The apply step converts to the Record shape `devcontainer.json`
 *     expects.
 *
 *   - `git.user.{name,email}` carries the host-captured identity so the
 *     yml-as-profile is self-contained when shared across containers.
 *     Optional — falls back to host-side `git config --global --get` +
 *     `.monoceros/gitconfig` at apply time, same as today.
 */

const SOLUTION_NAME_RE = /^[A-Za-z0-9._-]+$/;
const APT_PACKAGE_NAME_RE = /^[a-z0-9][a-z0-9.+-]*$/;
// Pinned runtime-image version: exact `major.minor.patch` only — no
// ranges/placeholders (ADR 0017). The image name/registry is a CLI
// constant; the yml carries just the version.
const RUNTIME_VERSION_RE = /^\d+\.\d+\.\d+$/;
// Feature refs are OCI-style:
//   <registry>/<namespace>/<feature>:<tag>
// e.g. ghcr.io/devcontainers/features/python:1
//      ghcr.io/getmonoceros/monoceros-features/claude-code:1
const FEATURE_REF_RE = /^[a-z0-9.-]+(\/[a-z0-9._-]+)+:[a-z0-9._-]+$/;
const INSTALL_URL_RE = /^https:\/\/[A-Za-z0-9.\-_~/:?#[\]@!&'()*+,;=%]+$/;
// Repo URLs are HTTPS-only by design. SSH-style URLs (git@host:...,
// ssh://...) are explicitly out of scope — see ADR 0006 for the
// reasoning. The schema rejects them at parse time with a clear
// message rather than letting them through and failing opaquely
// during the clone in post-create.sh.
const REPO_URL_RE = /^https:\/\/[A-Za-z0-9@:/+_~.#=&?-]+$/;
// Path under `projects/`. Allows nested subfolders via `/` (e.g.
// `apps/web`, `monorepo/libs/shared`). The regex enforces:
//   - non-empty
//   - segments use [A-Za-z0-9._-] (same charset as a leaf folder name)
//   - no leading `/`, no trailing `/`, no consecutive `//`
// A separate refine rejects `.` / `..` segments — those would either
// be no-ops or escape `projects/`, neither belongs in a checked-in
// container yml.
const REPO_PATH_RE = /^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/;

export const REGEX = {
  solutionName: SOLUTION_NAME_RE,
  aptPackage: APT_PACKAGE_NAME_RE,
  featureRef: FEATURE_REF_RE,
  installUrl: INSTALL_URL_RE,
  repoUrl: REPO_URL_RE,
  repoPath: REPO_PATH_RE,
};

/**
 * The Git providers Monoceros supports for repos.
 *
 * Canonical SaaS hostnames (`github.com` / `gitlab.com` /
 * `bitbucket.org`) auto-detect to their provider. A self-hosted host
 * (GitHub Enterprise, self-hosted GitLab, Bitbucket Data Center) must
 * declare `provider:` explicitly.
 *
 * Token-based repo auth is implemented for all three. GitHub/GitLab use
 * the `GIT_TOKEN__…` cascade (ADR 0031); Bitbucket is fronted by the
 * Atlassian feature (twg) and authenticates off the Atlassian API token
 * (ADR 0035).
 */
export const PROVIDER_VALUES = ['github', 'gitlab', 'bitbucket'] as const;
export type RepoProvider = (typeof PROVIDER_VALUES)[number];

/** Human-readable provider names for builder-facing messages. */
export const PROVIDER_LABEL: Record<RepoProvider, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
};

/**
 * The catalog selector for the CLI feature a repo of each provider pulls
 * in (ADR 0031 auto-add; ADR 0035 for Bitbucket). GitHub/GitLab map to
 * their single-purpose CLI; Bitbucket maps to the Atlassian feature's
 * `twg` preset (twg is the Bitbucket CLI, without Rovo/Forge). init and
 * add-repo both add this selector when a repo of the provider is
 * declared.
 */
export const PROVIDER_FEATURE_SELECTOR: Record<RepoProvider, string> = {
  github: 'github',
  gitlab: 'gitlab',
  bitbucket: 'atlassian/twg',
};

/**
 * The HTTPS git username paired with a provider token in
 * `.monoceros/git-credentials` (ADR 0031). GitHub/GitLab accept `oauth2`
 * with the token as the password; Bitbucket Cloud requires the static
 * literal `x-bitbucket-api-token-auth` for API-token git access.
 */
export const GIT_CREDENTIAL_USERNAME: Record<RepoProvider, string> = {
  github: 'oauth2',
  gitlab: 'oauth2',
  bitbucket: 'x-bitbucket-api-token-auth',
};

/** Docs page explaining how to set up a repo access token (ADR 0031). */
export const REPO_DOCS_URL =
  'https://getmonoceros.build/docs/concepts/git-and-repos/';

/**
 * Hostnames whose provider is implicit — no `provider:` field needed
 * in the yml. Everything else (self-hosted GitLab on `git.firma.de`,
 * Gitea instances, …) requires an explicit declaration; the apply
 * pre-flight enforces that.
 */
export const KNOWN_PROVIDER_HOSTS: Readonly<Record<string, RepoProvider>> = {
  'github.com': 'github',
  'gitlab.com': 'gitlab',
  'bitbucket.org': 'bitbucket',
};

/** Current schema version. Bumped only on breaking yml changes. */
export const CONFIG_SCHEMA_VERSION = 1 as const;

// Feature option values are string | number | boolean. We also
// accept `null` (yaml's parse result for a bare `key:`) and transform
// it to the empty string — that way an init-rendered options block
// like `apiKey:` parses cleanly and ends up equivalent to the feature
// option's documented default (which for string fields is `""`).
export const FeatureOptionValueSchema = z
  .union([z.string(), z.number(), z.boolean(), z.null()])
  .transform((v) => (v === null ? '' : v));

/**
 * One entry under a feature's `plugins:` — an agent plugin marketplace and
 * the plugins to install from it.
 *
 * A marketplace is either remote (`url`, an HTTPS git URL) or already in the
 * workspace (`path`, under `projects/`). Exactly one of the two. There is
 * deliberately no `owner/repo` shorthand: it silently means GitHub and leaves
 * a builder on GitLab or Bitbucket no way to say so. Same rule, same regex
 * and the same `provider:` escape hatch as `repos:`.
 *
 * No `ref:` either. The agent CLIs install a plugin from the marketplace's
 * default branch and offer no way to pin one, and the workbench takes the
 * same line with its features: tooling floats to what upstream ships (ADR
 * 0018). A pin the CLI cannot honor would be a lie in the yml.
 *
 * `enable` is required. A marketplace can hold several plugins, and
 * installing all of them because the builder named none is the wrong
 * default — our own marketplace would drag in a second plugin nobody asked
 * for.
 */
export const PluginEntrySchema = z
  .object({
    url: z
      .string()
      .regex(
        REPO_URL_RE,
        'Invalid plugin marketplace URL. Only HTTPS URLs are supported (https://...). SSH-style URLs (git@host:..., ssh://...) are not supported.',
      )
      .optional(),
    path: z
      .string()
      .regex(
        REPO_PATH_RE,
        "Invalid plugin marketplace path. Use a path under `projects/` with letters/digits/'._-', forward slashes for nested folders, no leading or trailing slash.",
      )
      .refine(
        (p) => !p.split('/').some((seg) => seg === '..' || seg === '.'),
        'Plugin marketplace path segments cannot be "." or "..".',
      )
      .optional(),
    // Same meaning as on a repo: needed only for a host that is not one of
    // the three canonical ones, so the apply pre-flight can name the right
    // token when credentials are missing.
    provider: z.enum(PROVIDER_VALUES).optional(),
    enable: z
      .array(z.string().min(1, 'Plugin name cannot be empty.'), {
        error:
          'Name the plugins to install from this marketplace, e.g. `enable: [acme-conventions]`.',
      })
      .min(1, 'List at least one plugin to install from this marketplace.'),
  })
  .superRefine((data, ctx) => {
    const hasUrl = data.url !== undefined;
    const hasPath = data.path !== undefined;
    if (hasUrl === hasPath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: hasUrl
          ? 'A plugin marketplace has either `url` or `path`, not both.'
          : 'A plugin marketplace needs `url` (an HTTPS git URL) or `path` (a folder under projects/).',
      });
    }
    if (hasPath && data.provider !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['provider'],
        message:
          '`provider` belongs to a `url` marketplace; a path in the workspace is not fetched.',
      });
    }
  });

export const FeatureEntrySchema = z.object({
  ref: z
    .string()
    .regex(
      FEATURE_REF_RE,
      "Invalid feature ref. Expected an OCI-image-style ref like 'ghcr.io/devcontainers/features/<name>:<tag>'.",
    ),
  options: z.record(z.string(), FeatureOptionValueSchema).optional(),
  // Agent plugins this feature should carry. Accepted on every feature
  // entry at parse time; whether the feature can actually host plugins is
  // a catalog question (`feature.acceptsPlugins` in the descriptor) and is
  // checked at apply, like every other catalog validation (ADR 0006).
  plugins: z.array(PluginEntrySchema).optional(),
});

/**
 * Git identity block.
 *
 * Both fields are nullable + accept the empty string. yaml allows
 * a bare `name:` (parsed as null) as a placeholder for "not set
 * yet — apply, please ask me". The resolution logic treats null /
 * empty as "unset" and walks the precedence chain further.
 *
 * The schema validates only the SHAPE (non-empty string). The email
 * FORMAT is deliberately NOT checked here: a value may be a `${VAR}`
 * placeholder resolved from `<name>.env` at apply time, and you can't
 * validate the format of a value you haven't resolved yet. The format
 * check moves to apply, after interpolation (see `isValidEmail` +
 * apply/index.ts). The flag entry points (`add-repo --git-email`)
 * validate eagerly there instead.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether a (resolved) string is a well-formed email address. */
export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

export const GitUserSchema = z.object({
  name: z
    .union([z.literal(''), z.null(), z.string().min(1)])
    .nullish()
    .transform((v) => (typeof v === 'string' && v.length > 0 ? v : undefined)),
  email: z
    .union([z.literal(''), z.null(), z.string().min(1)])
    .nullish()
    .transform((v) => (typeof v === 'string' && v.length > 0 ? v : undefined)),
});

export const RepoEntrySchema = z.object({
  url: z
    .string()
    .regex(
      REPO_URL_RE,
      'Invalid repo URL. Only HTTPS URLs are supported (https://...). SSH-style URLs (git@host:..., ssh://...) are not supported.',
    ),
  path: z
    .string()
    .regex(
      REPO_PATH_RE,
      "Invalid repo path. Use letters/digits/'._-', forward slashes for nested folders, no leading or trailing slash.",
    )
    .refine(
      (p) => !p.split('/').some((seg) => seg === '..' || seg === '.'),
      'Repo path segments cannot be "." or "..".',
    )
    .optional(),
  // Per-repo git identity override. Falls back to the container-level
  // `git.user` (which itself falls back to the host's
  // `git config --global` at apply time). Useful when a single
  // container clones multiple repos that need different committer
  // identities — e.g. work GitHub org vs personal projects.
  git: z
    .object({
      user: GitUserSchema.optional(),
    })
    .optional(),
  // Provider hint for the pre-flight credential check. For the three
  // canonical hosts (github.com / gitlab.com / bitbucket.org) the
  // provider is auto-detected and this field is unnecessary. For any
  // other host (self-hosted GitLab on a custom domain, Gitea, …) the
  // builder MUST declare the provider so apply can suggest the right
  // CLI setup (`glab auth login --hostname <host>` etc.) when
  // credentials are missing. Enforced at apply pre-flight, not at
  // parse time — see ADR 0006.
  provider: z.enum(PROVIDER_VALUES).optional(),
});

/**
 * A single entry under `ports:`. Two forms accepted:
 *
 *   ports:
 *     - 3000          # short form: just the port number
 *     - port: 9229    # long form, leaves room for future fields
 *                     # (protocol, path-prefix, entrypoint …)
 *
 * Today both forms carry the same information. The long form exists
 * so additive extensions (TLS entrypoint, path-based routing) don't
 * require a schema break. See ADR 0007.
 */
export const PortEntrySchema = z.union([
  z
    .number()
    .int()
    .min(1, 'Port must be ≥ 1.')
    .max(65535, 'Port must be ≤ 65535.'),
  z.object({
    port: z
      .number()
      .int()
      .min(1, 'Port must be ≥ 1.')
      .max(65535, 'Port must be ≤ 65535.'),
  }),
]);

/**
 * Routing block — everything Monoceros uses to expose container ports
 * to the host through the shared Traefik singleton.
 *
 *   - `ports`: container-internal ports the builder wants reachable
 *     via `<name>.localhost` / `<name>-<port>.localhost`. Short form
 *     `3000` or long form `{ port: 3000 }` are both accepted; mutators
 *     write the short form by default. First port doubles as the
 *     default route under the bare `<name>.localhost`.
 *
 *   - `vscodeAutoForward`: whether VS Code's Dev-Containers extension
 *     should also auto-forward ports on top of Traefik. Default
 *     `false`. Set to `true` only if VS Code's port panel should be
 *     the primary entry rather than `<name>.localhost`.
 *
 * Host-port for the Traefik singleton itself is global (one Traefik
 * per machine), not per container — it lives in `monoceros-config.yml`
 * under `routing.hostPort`. See ADR 0007.
 */
export const RoutingSchema = z.object({
  ports: z.array(PortEntrySchema).default([]),
  vscodeAutoForward: z.boolean().optional(),
});

// ── Services ────────────────────────────────────────────────────────
//
// A `services:` entry is always an object: `image` is required; env,
// volumes, port, healthcheck, restart and command are opt-in.
//
// There is deliberately ONE form. The curated catalog (postgres / mysql
// / redis) is init-sugar: `monoceros init --with-services=postgres` and
// `monoceros add-service <name> postgres` EXPAND the catalog name into a
// full, editable object block. The bare-string form is NOT accepted in
// the yml — a single shape keeps the model and the docs unambiguous.
// See docs/backlog.md (init + service redesign).

// Compose service name: becomes the docker compose service key, the
// in-network DNS alias other services dial it by, and the per-service
// data dir name under container/<name>/data/<svc>/.
const SERVICE_NAME_RE = /^[a-z0-9][a-z0-9_-]*$/;

// env values: yaml may parse a value as string/number/bool, or a bare
// `KEY:` as null. Coerce everything to a string for compose output;
// null → "" (an explicitly empty env var). `${VAR}` references survive
// verbatim here — they are resolved against `<name>.env` at apply time.
const ServiceEnvValueSchema = z
  .union([z.string(), z.number(), z.boolean(), z.null()])
  .transform((v) => (v === null ? '' : String(v)));

export const ServiceHealthcheckSchema = z.object({
  // Compose accepts both forms and they differ semantically:
  //   - string         → run via the shell (CMD-SHELL)
  //   - ["CMD", …]      → exec the args directly, no shell
  //   - ["CMD-SHELL", …]
  // We accept either and render it back faithfully.
  test: z.union([
    z.string().min(1, 'Healthcheck test must not be empty.'),
    z
      .array(z.string().min(1))
      .min(1, 'Healthcheck test array must not be empty.'),
  ]),
  interval: z.string().optional(),
  timeout: z.string().optional(),
  retries: z.number().int().min(1).optional(),
  startPeriod: z.string().optional(),
});

export const SERVICE_RESTART_VALUES = [
  'no',
  'always',
  'on-failure',
  'unless-stopped',
] as const;

// A volume entry is `src:dest[:mode]`.
//
//   - `src` is either the `data` shorthand (→ the per-service
//     bind-mounted data dir under container/<name>/data/<name>/) or a
//     path relative to the container root (`projects/app/init.sql`,
//     `./config/x`). `dest` is an absolute in-container path.
//   - Docker **named volumes** (a bare token like `rustfs_data`) are NOT
//     supported — Monoceros binds to the host disk so content is part of
//     backups (ADR 0003). A bare single token is rejected with a hint to
//     use `data:` or an explicit relative path, because that's the most
//     common compose-port mistake.
//   - Absolute host sources and `..` escapes are rejected.
function isValidServiceVolume(spec: string): boolean {
  const parts = spec.split(':');
  if (parts.length < 2 || parts.length > 3) return false;
  const [src, dest, mode] = parts;
  if (!src || !dest) return false;
  if (!dest.startsWith('/')) return false; // container target must be absolute
  if (mode !== undefined && !/^(ro|rw|cached|delegated|z|Z)$/.test(mode)) {
    return false;
  }
  if (src === 'data') return true;
  if (src.startsWith('/')) return false; // absolute host source
  // A bare token (no `./`, no `/`) is almost always a leftover docker
  // named volume — reject it rather than silently bind-mounting a junk
  // host dir of that name.
  const looksLikePath = src.startsWith('./') || src.includes('/');
  if (!looksLikePath) return false;
  const normalized = src.startsWith('./') ? src.slice(2) : src;
  if (normalized.split('/').some((s) => s === '..' || s === '.')) return false;
  return true;
}

export const ServiceObjectSchema = z.object({
  name: z
    .string()
    .regex(
      SERVICE_NAME_RE,
      "Invalid service name. Use lowercase letters, digits, '_' or '-' (must start with a letter or digit).",
    ),
  image: z.string().min(1, 'Service image must not be empty.'),
  // Compose `user:` for the service container (e.g. "0:0"). Set for images
  // that run as a fixed non-root uid but must write a host bind-mounted
  // dataMount — without it they can't write the apply-created data dir on
  // native Linux and exit (e.g. rustfs). Curated entries bake this from the
  // catalog; a builder can also set it by hand.
  user: z.string().min(1).optional(),
  // In-container port the service listens on. Used by
  // `monoceros tunnel <name> <service>` to forward without an explicit
  // port argument. NOT a host port mapping — host exposure goes through
  // routing.ports (Traefik) or `monoceros tunnel`.
  port: z.number().int().min(1, 'Port must be ≥ 1.').max(65535).optional(),
  // The service's HTTP port that may leave the container: `monoceros share`
  // offers it on the LAN over https, and the proxy routes to it. Curated
  // entries bake it from the catalog (only where it makes sense - never on a
  // database); removing the line keeps this workbench's instance to itself.
  // A hand-written service can set it too. Not a host port mapping.
  httpPort: z.number().int().min(1, 'Port must be ≥ 1.').max(65535).optional(),
  env: z.record(z.string(), ServiceEnvValueSchema).optional(),
  volumes: z
    .array(
      z
        .string()
        .refine(
          isValidServiceVolume,
          "Invalid volume. Use 'data:/container/path' for the per-service persistent dir, or a relative host path ('projects/app/init.sql:/...:ro', './config:/...'). Docker named volumes (a bare name like 'rustfs_data') are not supported; absolute host paths and '..' are rejected.",
        ),
    )
    .optional(),
  healthcheck: ServiceHealthcheckSchema.optional(),
  restart: z.enum(SERVICE_RESTART_VALUES).optional(),
  command: z.string().optional(),
  // Connection-env templates keyed by logical SUFFIX (URL/HOST/PORT/USER/
  // PASSWORD/DB). Baked in by `expandCuratedService`; emitted into the
  // workspace as `<UPPER(name)>_<SUFFIX>` at apply (ADR 0021). `${host}`/
  // `${port}`/`${<OPTION>}` are render tokens, NOT `.env` placeholders, and
  // are left untouched by `interpolateServices`.
  connectionEnv: z.record(z.string(), z.string()).optional(),
});

// ── MCP servers ─────────────────────────────────────────────────────
//
// `mcpServers:` lists the servers the agents in this container can reach. Two
// forms in ONE array, and what tells them apart is not the name but
// whether the entry carries a definition (ADR 0045):
//
//   mcpServers:
//     - name: context7            ← resolved against the catalog/overlay
//       options:
//         apiKey: ${CONTEXT7_API_KEY}
//     - name: notion              ← stands for itself, nothing resolved
//       transport: stdio
//       command: npx
//       args: ['-y', '@notionhq/notion-mcp-server']
//       env:
//         NOTION_TOKEN: ${NOTION_TOKEN}
//
// The second form is how the long tail gets in: the catalog will only
// ever curate a handful, and everything else is pasted from the config
// its provider publishes. `options:` is meaningless there (there is no
// descriptor to validate an option against), so the two are exclusive.
//
// A name is NOT checked against the catalog here — that happens at apply,
// like every other catalog validation. Which also means an inline entry
// keeps standing for itself even after a curated connector of the same
// name ships, so a working yml never changes meaning under the builder.

// MCP server name: becomes the key in each agent's config. Wider charset
// than a component id because these names come from other people's
// READMEs (`chrome-devtools`, `sentry_v2`, `atlassian.mcp`).
const MCP_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * How an MCP server is reached. `stdio` runs it as a process INSIDE the
 * workspace container, so it stays behind the container boundary; `http`
 * and `sse` talk to a remote endpoint, so it does not.
 *
 * Lives here rather than in the catalog descriptor because both shapes need
 * it: a curated connector's `mcpServer:` block and an inline entry in a container
 * yml are the same thing said in two places — and the descriptor already
 * imports from this module.
 */
export const MCP_TRANSPORTS = ['stdio', 'http', 'sse'] as const;
export const McpTransportSchema = z.enum(MCP_TRANSPORTS);
export type McpTransport = z.infer<typeof McpTransportSchema>;

/**
 * A transport dictates which fields carry meaning, and a field belonging to
 * the other transport is a silent no-op rather than a typo you find out
 * about. So: require what the transport needs, reject what it cannot use.
 */
export function validateMcpTransportFields(
  data: {
    transport: McpTransport;
    command?: string | undefined;
    args?: readonly string[] | undefined;
    env?: Record<string, string> | undefined;
    url?: string | undefined;
    headers?: Record<string, string> | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const stdioOnly = ['command', 'args', 'env'] as const;
  const remoteOnly = ['url', 'headers'] as const;
  if (data.transport === 'stdio') {
    if (data.command === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['command'],
        message: "transport 'stdio' requires `command`",
      });
    }
    for (const field of remoteOnly) {
      if (data[field] !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `\`${field}\` has no meaning for transport 'stdio'`,
        });
      }
    }
    return;
  }
  if (data.url === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['url'],
      message: `transport '${data.transport}' requires \`url\``,
    });
  }
  for (const field of stdioOnly) {
    if (data[field] !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: `\`${field}\` has no meaning for transport '${data.transport}'`,
      });
    }
  }
}

const MCP_DEFINITION_FIELDS = [
  'transport',
  'command',
  'args',
  'env',
  'url',
  'headers',
] as const;

const McpStringMapSchema = z.record(
  z.string(),
  z
    .union([z.string(), z.number(), z.boolean(), z.null()])
    .transform((v) => (v === null ? '' : String(v))),
);

export const McpEntrySchema = z
  .object({
    name: z
      .string()
      .regex(
        MCP_NAME_RE,
        "Invalid MCP server name. Use letters, digits, '.', '_' or '-' (must start with a letter or digit).",
      ),
    options: z.record(z.string(), FeatureOptionValueSchema).optional(),
    transport: McpTransportSchema.optional(),
    command: z.string().min(1).optional(),
    args: z.array(z.string()).optional(),
    env: McpStringMapSchema.optional(),
    url: z.string().min(1).optional(),
    headers: McpStringMapSchema.optional(),
  })
  .superRefine((entry, ctx) => {
    const inline = MCP_DEFINITION_FIELDS.some(
      (field) => entry[field] !== undefined,
    );
    if (!inline) return; // a bare reference — apply resolves it
    if (entry.options !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message:
          '`options` belongs to a catalog connector; an inline server has no descriptor to validate them against. Drop `options:` or drop the definition fields and keep just the name.',
      });
    }
    if (entry.transport === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transport'],
        message: `An inline MCP server needs \`transport\` (one of: ${MCP_TRANSPORTS.join(', ')}).`,
      });
      return;
    }
    validateMcpTransportFields({ ...entry, transport: entry.transport }, ctx);
  });

// A language entry is either the bare/`:version` string form (`node`,
// `java:17`) or an object form that surfaces feature options in the yml:
//   - java:
//       version: 21
//       installMaven: true
// The object form is a single-key map `{ <language>: { ...options } }` where
// `version` (if present) is the upstream feature version and the rest are
// feature options. Backward compatible: existing string entries still parse.
const LanguageOptionValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);
const LanguageEntrySchema = z.union([
  z.string().min(1),
  z
    .record(
      z.string().min(1),
      z.record(z.string().min(1), LanguageOptionValueSchema),
    )
    .refine((obj) => Object.keys(obj).length === 1, {
      message:
        'a language entry object must have exactly one language name as its key',
    }),
]);

export const SolutionConfigSchema = z
  .object({
    schemaVersion: z.literal(CONFIG_SCHEMA_VERSION),
    name: z
      .string()
      .regex(
        SOLUTION_NAME_RE,
        "Invalid solution name. Use letters, digits, '.', '_' or '-'.",
      ),
    // Pinned runtime-image version (ADR 0017). Written by `init`, reused
    // verbatim by every subsequent `apply` (never auto-bumped), changed
    // only by `monoceros upgrade`. Optional in the schema so a
    // pre-pinning yml still parses — `apply` is what enforces its
    // presence with an actionable hint.
    runtimeVersion: z
      .string()
      .regex(
        RUNTIME_VERSION_RE,
        "Invalid runtimeVersion. Expected an exact version like '1.1.0'.",
      )
      .optional(),
    languages: z.array(LanguageEntrySchema).default([]),
    aptPackages: z
      .array(
        z
          .string()
          .regex(
            APT_PACKAGE_NAME_RE,
            "Invalid apt package name. Expected lowercase alphanumeric plus '.+-'.",
          ),
      )
      .default([]),
    features: z.array(FeatureEntrySchema).default([]),
    installUrls: z
      .array(
        z
          .string()
          .regex(
            INSTALL_URL_RE,
            "Invalid install URL. Must start with 'https://' and contain only URL-safe characters (no shell metacharacters).",
          ),
      )
      .default([]),
    services: z.array(ServiceObjectSchema).default([]),
    mcpServers: z.array(McpEntrySchema).default([]),
    repos: z.array(RepoEntrySchema).default([]),
    routing: RoutingSchema.optional(),
    git: z
      .object({
        user: GitUserSchema.optional(),
      })
      .optional(),
  })
  .superRefine((cfg, ctx) => {
    // Service names must be unique: each becomes a compose service key, a DNS
    // alias, a data dir, and the prefix for that instance's connection env
    // (ADR 0021). Duplicates would silently clobber one another.
    const seen = new Set<string>();
    cfg.services.forEach((svc, i) => {
      if (seen.has(svc.name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services', i, 'name'],
          message: `Duplicate service name '${svc.name}'. Service names must be unique (use \`add-service … --as <name>\` to add a second instance of the same image).`,
        });
      }
      seen.add(svc.name);
    });

    // MCP names must be unique: each becomes one key in the agent's config,
    // so a duplicate would quietly overwrite the other and the builder would
    // be looking at a yml that says something the container does not do.
    const seenMcp = new Set<string>();
    cfg.mcpServers.forEach((entry, i) => {
      if (seenMcp.has(entry.name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mcpServers', i, 'name'],
          message: `Duplicate MCP server name '${entry.name}'. Each server is registered once, under its name.`,
        });
      }
      seenMcp.add(entry.name);
    });
  });

export type SolutionConfig = z.infer<typeof SolutionConfigSchema>;
export type FeatureEntry = z.infer<typeof FeatureEntrySchema>;
export type PluginEntry = z.infer<typeof PluginEntrySchema>;
export type McpEntry = z.infer<typeof McpEntrySchema>;
export type ServiceObject = z.infer<typeof ServiceObjectSchema>;
export type ServiceHealthcheck = z.infer<typeof ServiceHealthcheckSchema>;
export type RepoEntry = z.infer<typeof RepoEntrySchema>;
export type GitUser = z.infer<typeof GitUserSchema>;
export type PortEntry = z.infer<typeof PortEntrySchema>;
export type Routing = z.infer<typeof RoutingSchema>;

/** Resolve a `PortEntry` (short or long form) to a plain port number. */
export function portNumber(entry: PortEntry): number {
  return typeof entry === 'number' ? entry : entry.port;
}

/**
 * Validate parsed yml (e.g. from `doc.toJS()`) against the schema. On
 * failure, throws an Error whose message lists every issue with its
 * dotted path — the apply step prints that verbatim, so the builder
 * sees exactly which yml field is wrong.
 */
export function validateConfig(input: unknown): SolutionConfig {
  const result = SolutionConfigSchema.safeParse(input);
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => {
        const where = issue.path.length > 0 ? issue.path.join('.') : '(root)';
        return `  - ${where}: ${issue.message}`;
      })
      .join('\n');
    throw new Error(`Invalid solution config:\n${issues}`);
  }
  return result.data;
}
